// Mathematik des Mehrsegment-Rings („Donut") neben einer konsolidierten
// Betragszelle: Bogenlängen, Lücken und die ehrliche Prozentverteilung.
//
// Diese Datei ist bewusst REACT-FREI — kein Import aus react oder @fluentui.
// Sie ist reine Geometrie und muss in der node-Testumgebung ohne DOM laufen.

import { KZ_REST } from "./scopeColors";

/** Außenmaß des Rings in px — passt in eine Tabellenzeile neben den Betrag. */
export const RING_SIZE = 22;
/** Strichstärke; der Ring hat bewusst keine Füllung (nur Rand, wie ein Spinner). */
export const RING_STROKE = 4;
/**
 * Sichtbare Lücke zwischen zwei Segmenten (in px Bogenlänge). Ohne sie
 * verschmelzen zwei ähnlich helle Nachbarfarben zu einem Bogen.
 */
export const RING_GAP = 1.6;

/** Kürzester Bogen, der noch als Segment lesbar ist — darunter bleibt nur ein Punkt. */
const MIN_ARC = 2;
/** Ab wie vielen Segmenten zu „Sonstige" verdichtet wird. */
const MAX_SEGMENTS = 8;
/** Anteil, unterhalb dessen ein Segment als Kleinstanteil gilt. */
const MIN_SHARE = 0.02;

const RADIUS = (RING_SIZE - RING_STROKE) / 2;
const CIRC = 2 * Math.PI * RADIUS;

/** Kürzt die Nachkommastellen, damit im DOM keine 15-stelligen Floats landen. */
const r3 = (n: number): number => Math.round(n * 1000) / 1000;

/** Ein Ringsegment = der Beitrag einer Firma (oder das gesammelte „Sonstige"). */
export type RingSegment = { contactId: number; name: string; value: number; color: string };

/** Ein gezeichneter Bogen — direkt als SVG-`stroke-dash*`-Attribute verwendbar. */
export type RingArc = { color: string; dashArray: string; dashOffset: number };

/** Sentinel-Id des zusammengefassten Segments; keine echte `contactId` ist negativ. */
export const REST_CONTACT_ID = -1;
export const REST_NAME = "Sonstige";

/**
 * Rechnet Anteile (0..1, werden intern normiert) in `stroke-dasharray`/
 * `-dashoffset`-Paare um.
 *
 * Jedes Segment bekommt seine Bogenlänge plus eine Lücke; der Offset ist die
 * kumulierte Strecke davor und wird NEGATIV gesetzt, weil `stroke-dashoffset`
 * das Muster gegen die Zeichenrichtung verschiebt.
 */
export function buildArcs(shares: number[], colors: string[]): RingArc[] {
  const n = shares.length;
  if (n === 0) return [];
  const summe = shares.reduce((a, s) => a + Math.max(0, s), 0);
  if (summe <= 0) return [];
  const norm = shares.map((s) => Math.max(0, s) / summe);

  if (n === 1) {
    // Bei genau einem Segment gibt es nichts zu trennen: eine Lücke wäre keine
    // Segmentgrenze, sondern nur eine Kerbe im geschlossenen Ring.
    return [{ color: colors[0] ?? KZ_REST, dashArray: `${r3(CIRC)} 0`, dashOffset: 0 }];
  }

  const verfuegbar = CIRC - n * RING_GAP;
  let laengen = norm.map((s) => Math.max(MIN_ARC, s * verfuegbar));
  // Die Mindestbögen können die verfügbare Strecke sprengen (viele Winzlinge).
  // Die Differenz kommt anteilig von den Segmenten zurück, die über dem
  // Mindestmaß liegen — sonst überliefe das letzte Segment den Ringanfang.
  const ueberhang = laengen.reduce((a, l) => a + l, 0) - verfuegbar;
  if (ueberhang > 0) {
    const kuerzbar = laengen.reduce((a, l) => a + Math.max(0, l - MIN_ARC), 0);
    if (kuerzbar > 0) {
      laengen = laengen.map((l) => l - (Math.max(0, l - MIN_ARC) / kuerzbar) * ueberhang);
    }
  }

  const arcs: RingArc[] = [];
  let pos = 0;
  for (let i = 0; i < n; i += 1) {
    const len = laengen[i];
    arcs.push({
      color: colors[i] ?? KZ_REST,
      dashArray: `${r3(len)} ${r3(CIRC - len)}`,
      // `|| 0` glättet das -0 des ersten Segments (React schriebe "-0").
      dashOffset: -r3(pos) || 0,
    });
    pos += len + RING_GAP;
  }
  return arcs;
}

/**
 * Verdichtet die Firmenbeiträge auf eine zeichenbare Segmentzahl.
 *
 * Kleinstanteile unter `minShare` wandern nur dann nach „Sonstige", wenn es
 * MINDESTENS ZWEI davon gibt und mindestens eine Firma sichtbar bleibt: eine
 * einzelne Kleinstfirma hinter einer grauen Sammelfarbe zu verstecken sagt
 * weniger aus, als sie mit ihrem dünnen Bogen zu zeigen — und ein Ring, der nur
 * noch aus „Sonstige" besteht, sagt gar nichts mehr.
 *
 * Die Zusammenfassung ist rein visuell: `restMembers` liefert die verdichteten
 * Firmen zurück, damit das Popover sie einzeln aufklappen kann.
 */
export function aggregateSegments(
  segs: RingSegment[],
  maxSegments: number = MAX_SEGMENTS,
  minShare: number = MIN_SHARE,
): { shown: RingSegment[]; rest: RingSegment | null; restMembers: RingSegment[] } {
  if (segs.length === 0) return { shown: [], rest: null, restMembers: [] };

  const total = segs.reduce((a, s) => a + s.value, 0);
  let kept = [...segs].sort((a, b) => b.value - a.value);
  const restMembers: RingSegment[] = [];

  if (total > 0) {
    const winzig = kept.filter((s) => s.value / total < minShare);
    if (winzig.length >= 2 && winzig.length < kept.length) {
      restMembers.push(...winzig);
      kept = kept.filter((s) => s.value / total >= minShare);
    }
  }

  // „Sonstige" belegt selbst ein Segment — die Obergrenze gilt inklusive.
  if (kept.length + (restMembers.length > 0 ? 1 : 0) > maxSegments) {
    const grenze = Math.max(1, maxSegments - 1);
    restMembers.push(...kept.slice(grenze));
    kept = kept.slice(0, grenze);
  }

  if (restMembers.length === 0) return { shown: kept, rest: null, restMembers: [] };

  restMembers.sort((a, b) => b.value - a.value);
  const rest: RingSegment = {
    contactId: REST_CONTACT_ID,
    name: REST_NAME,
    value: restMembers.reduce((a, s) => a + s.value, 0),
    color: KZ_REST,
  };
  // `rest` gehört IMMER ans Ende der Segmentliste, damit der graue Sammelbogen
  // nicht zwischen zwei Firmenfarben sitzt.
  return { shown: kept, rest, restMembers };
}

/**
 * Ganzzahlige Prozente, deren Summe exakt 100 ergibt (Hare-/Largest-Remainder).
 *
 * Einzeln gerundete Prozente summieren sich regelmäßig auf 99 oder 101 — in
 * einer Aufteilungs-Ansicht sieht das schlicht nach einem Rechenfehler aus.
 * Erwartet nicht-negative Werte; ohne verwertbare Summe kommen lauter Nullen.
 */
export function largestRemainderPercents(values: number[]): number[] {
  const n = values.length;
  if (n === 0) return [];
  const total = values.reduce((a, v) => a + Math.max(0, v), 0);
  if (total <= 0) return values.map(() => 0);

  const exakt = values.map((v) => (Math.max(0, v) / total) * 100);
  const ganz = exakt.map((e) => Math.floor(e));
  let offen = 100 - ganz.reduce((a, g) => a + g, 0);

  // Die größten Nachkommareste bekommen die verbleibenden Punkte; bei Gleichstand
  // gewinnt der frühere Index, damit die Verteilung reproduzierbar bleibt.
  const rang = exakt
    .map((e, i) => ({ i, rest: e - Math.floor(e) }))
    .sort((a, b) => b.rest - a.rest || a.i - b.i);
  for (const { i } of rang) {
    if (offen <= 0) break;
    ganz[i] += 1;
    offen -= 1;
  }
  return ganz;
}
